import { Link } from 'react-router-dom';
import {
  Users,
  Gauge,
  Maximize,
  Home,
  Building2,
  Layers,
  ShieldCheck,
  Box,
  AlertTriangle,
  FileQuestion,
  Activity,
  Map as MapIcon,
} from 'lucide-react';
import { IndicatorCard } from './IndicatorCard';
import { DpeHistogram, OccupationDonut } from './ChartPanel';
import { useBuildingStats, useIndicators } from '@/hooks/useIndicators';
import { useIngestionStatus, isIngestionActive } from '@/hooks/useIngestion';
import { useTerritoryStore } from '@/stores/territoryStore';
import { MapView2D } from '@/components/map/MapView2D';
import type { Indicator } from '@/types/indicator';

// ── Signals (derived from real building stats) ───────────────────────────────

type SignalTone = 'danger' | 'warn' | 'info' | 'ok';

const SIGNAL_TONE: Record<SignalTone, { color: string; bg: string }> = {
  danger: { color: '#e1000f', bg: '#fdecec' },
  warn: { color: '#ef7d18', bg: '#fff4e5' },
  info: { color: '#2563eb', bg: '#eaf2fb' },
  ok: { color: '#1f8a5b', bg: '#eaf6ef' },
};

interface Signal {
  icon: typeof AlertTriangle;
  tone: SignalTone;
  title: string;
  text: string;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function findIndicator(indicators: Indicator[], ...names: string[]): Indicator | undefined {
  return indicators.find((i) => names.includes(i.name));
}

function findIndicatorByKeyword(indicators: Indicator[], re: RegExp): Indicator | undefined {
  return indicators.find((i) => re.test(i.name));
}

/** Numeric value formatted in FR locale, or the 'N/D' placeholder. */
function kpiValue(v: number | null | undefined): string | number {
  return v == null ? 'N/D' : v;
}

// ── Component ────────────────────────────────────────────────────────────────

export function TerritoryStats() {
  const { activeTerritory } = useTerritoryStore();
  const insee = activeTerritory?.code_insee ?? null;
  const stats = useBuildingStats(insee);
  const indicators = useIndicators(insee);
  const ingest = useIngestionStatus(insee);
  const ingesting = isIngestionActive(ingest.data?.status);

  // ── Empty: no territory selected ───────────────────────────────────────────
  if (!activeTerritory) {
    return (
      <div className="flex h-full items-center justify-center p-8">
        <p className="font-sans text-sm text-muted">
          Sélectionnez un territoire pour afficher le tableau de bord.
        </p>
      </div>
    );
  }

  const loading = stats.isLoading || indicators.isLoading;
  const error = stats.isError || indicators.isError;

  // ── Derived data ───────────────────────────────────────────────────────────
  const inds = indicators.data ?? [];
  const stat = stats.data;
  const dist = stat?.dpe_distribution ?? {};
  const byNature = stat?.by_nature ?? {};
  const totalBuildings = stat?.total_count ?? 0;

  const densityInd = findIndicator(inds, 'population_density');
  const density =
    densityInd?.value ??
    (activeTerritory.population != null && activeTerritory.surface_ha
      ? Math.round(activeTerritory.population / (activeTerritory.surface_ha / 100))
      : null);

  const logementInd = findIndicatorByKeyword(inds, /logement/i);
  const artifInd = findIndicatorByKeyword(inds, /artificialis/i);
  // Bâtiments : compte réel issu des stats bâti (0 ⇒ non encore ingéré).
  const nbBatiments = totalBuildings > 0 ? totalBuildings : null;

  const superficie =
    activeTerritory.surface_ha != null ? Math.round(activeTerritory.surface_ha) : null;

  // Cellule KPI dépendante des données lourdes : valeur réelle, sinon
  // « Calcul… » (pulse) pendant l'ingestion, sinon « N/D ».
  const dataCell = (v: number | null | undefined) =>
    v != null
      ? { value: v as string | number, loading: false }
      : { value: ingesting ? 'Calcul…' : 'N/D', loading: ingesting };

  // Signals
  const sumDpe = Object.values(dist).reduce((sum, n) => sum + n, 0);
  const passoires = (dist.F ?? 0) + (dist.G ?? 0);
  const passoiresPct = sumDpe > 0 ? Math.round((passoires / sumDpe) * 100) : 0;
  const noDpe = Math.max(0, totalBuildings - sumDpe);
  const noDpePct = totalBuildings > 0 ? Math.round((noDpe / totalBuildings) * 100) : 0;

  const signals: Signal[] = [
    {
      icon: AlertTriangle,
      tone: passoiresPct > 15 ? 'danger' : passoiresPct > 0 ? 'warn' : 'ok',
      title: 'Passoires énergétiques',
      text:
        sumDpe > 0
          ? `${passoiresPct}% du parc diagnostiqué classé F ou G`
          : 'Aucun diagnostic DPE disponible',
    },
    {
      icon: FileQuestion,
      tone: noDpePct > 50 ? 'warn' : 'info',
      title: 'Couverture du diagnostic',
      text: `${noDpePct}% des bâtiments sans étiquette DPE renseignée`,
    },
    {
      icon: Activity,
      tone: 'info',
      title: 'Densité de population',
      text:
        density != null
          ? `${Math.round(density).toLocaleString('fr-FR')} hab/km²`
          : 'Donnée non disponible',
    },
  ];

  return (
    <div className="flex flex-col gap-4 p-5">
      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-[25px] font-extrabold tracking-tight text-ink">
            Tableau de bord
          </h1>
          <p className="text-[12.5px] text-muted">
            {activeTerritory.name} ·{' '}
            <span className="font-mono">INSEE {activeTerritory.code_insee}</span>
          </p>
        </div>
        <div className="flex items-center gap-2">
          {activeTerritory.has_full_data === false ? (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-[#fff4e5] px-3 py-1 text-[12px] font-semibold text-[#ef7d18]">
              <Activity size={14} aria-hidden="true" />
              {ingesting ? 'Chargement des données…' : 'Données de base'}
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-[#eaf6ef] px-3 py-1 text-[12px] font-semibold text-[#1f8a5b]">
              <ShieldCheck size={14} aria-hidden="true" />
              Données vérifiées
            </span>
          )}
          <Link
            to="/3d"
            className="inline-flex items-center gap-1.5 rounded-[9px] bg-navy px-3 py-1.5 text-[12.5px] font-semibold text-white transition hover:opacity-90"
          >
            <Box size={14} aria-hidden="true" />
            Basculer en 3D
          </Link>
        </div>
      </div>

      {/* ── Error ───────────────────────────────────────────────────────────── */}
      {error ? (
        <div
          role="alert"
          className="rounded-panel border px-5 py-4"
          style={{ background: '#fdecec', borderColor: '#f5c2c5', color: '#e1000f' }}
        >
          <p className="font-display text-[14px] font-bold">Données indisponibles</p>
          <p className="text-[12.5px]">
            Le chargement des indicateurs du territoire a échoué. Réessayez ultérieurement.
          </p>
        </div>
      ) : loading ? (
        // ── Loading ──────────────────────────────────────────────────────────
        <div className="flex flex-col gap-4" aria-busy="true">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-5">
            {Array.from({ length: 5 }).map((_, i) => (
              <div
                key={i}
                className="h-[92px] animate-pulse rounded-card border border-line bg-white"
              />
            ))}
          </div>
          <div className="flex items-center justify-center py-16">
            <p className="font-sans text-sm text-muted animate-pulse">
              Chargement des indicateurs…
            </p>
          </div>
        </div>
      ) : (
        <>
          {/* ── KPI row (6) ───────────────────────────────────────────────────── */}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-6">
            <IndicatorCard
              label="Population"
              value={kpiValue(activeTerritory.population)}
              unit="hab."
              icon={<Users size={15} />}
            />
            <IndicatorCard
              label="Densité"
              value={density != null ? Math.round(density) : 'N/D'}
              unit="hab/km²"
              icon={<Gauge size={15} />}
            />
            <IndicatorCard
              label="Superficie"
              value={kpiValue(superficie)}
              unit="ha"
              icon={<Maximize size={15} />}
            />
            <IndicatorCard
              label="Bâtiments"
              {...dataCell(nbBatiments)}
              unit="bât."
              icon={<Building2 size={15} />}
            />
            <IndicatorCard
              label="Logements"
              {...dataCell(logementInd?.value)}
              unit={logementInd?.unit ?? 'logts'}
              icon={<Home size={15} />}
            />
            <IndicatorCard
              label="Sols artificialisés"
              {...dataCell(artifInd?.value)}
              unit={artifInd?.unit ?? '%'}
              icon={<Layers size={15} />}
            />
          </div>

          {/* ── Grid 1.62fr / 1fr ─────────────────────────────────────────────── */}
          <div className="grid items-start gap-4 grid-cols-1 lg:grid-cols-[1.62fr_1fr]">
            {/* Analysis map */}
            <div className="flex flex-col overflow-hidden rounded-panel border border-line bg-white shadow-card">
              <div className="flex items-center justify-between border-b border-line px-4 py-3">
                <div>
                  <h3 className="font-display text-[14px] font-bold text-ink">Carte d&apos;analyse</h3>
                  <p className="text-[11.5px] text-faint">
                    Bâti, voirie et couches territoriales
                  </p>
                </div>
                <MapIcon size={15} className="text-faint" aria-hidden="true" />
              </div>
              <div className="relative" style={{ height: 480 }}>
                <MapView2D />
              </div>
            </div>

            {/* Right column */}
            <div className="flex flex-col gap-4">
              <DpeHistogram distribution={dist} ingesting={ingesting} />
              <OccupationDonut data={byNature} ingesting={ingesting} />

              {/* Signals & alerts */}
              <div className="rounded-panel border border-line bg-white shadow-card">
                <div className="border-b border-line px-4 py-3">
                  <h3 className="font-display text-[14px] font-bold text-ink">
                    Signaux &amp; alertes
                  </h3>
                  <p className="text-[11.5px] text-faint">Points de vigilance du territoire</p>
                </div>
                <ul className="divide-y divide-line">
                  {signals.map((s) => {
                    const tone = SIGNAL_TONE[s.tone];
                    const Icon = s.icon;
                    return (
                      <li key={s.title} className="flex items-start gap-3 px-4 py-3">
                        <span
                          className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md"
                          style={{ background: tone.bg, color: tone.color }}
                          aria-hidden="true"
                        >
                          <Icon size={14} />
                        </span>
                        <div className="min-w-0 flex-1">
                          <p className="text-[13px] font-semibold text-ink">{s.title}</p>
                          <p className="text-[11.5px] text-muted">{s.text}</p>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
          </div>

          {/* Sources */}
          <p className="pb-1 text-[10.5px] text-faint">
            Sources : INSEE (population, densité), BD TOPO (bâti, voirie), BDNB (DPE).
            Les indicateurs Logements et Sols artificialisés s&apos;affichent dès leur calcul
            par le backend.
          </p>
        </>
      )}
    </div>
  );
}
